'use client';

import { Box, Popover } from '@mui/material';
import React, { FC, useCallback, useMemo, useState } from 'react';
import LibraryAddCheckOutlinedIcon from '@mui/icons-material/LibraryAddCheckOutlined';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  PinterestIcon,
  PinterestShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from 'react-share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ButtonCopyStyled, ShareBtnBox } from './styled';
import { useTranslations } from 'next-intl';
import { pushClickSocial } from '@/lib/analytics';

type ShareModalProps = {
  id?: string;
  trigger?: React.ReactNode;
  title?: string;
  description?: string;
  media?: string;
  slugLink: string;
  onToggle?: (value: boolean) => void;
  open?: boolean;
  onClose?: () => void;
  anchorEl?: HTMLElement;
};

export const ShareModal: FC<ShareModalProps> = ({
  id,
  title = '',
  description,
  trigger,
  media,
  slugLink,
  onToggle,
  open: openProp,
  onClose,
  anchorEl: anchorElProp,
}) => {
  const t = useTranslations('share');
  const [isCopied, setIsCopied] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : Boolean(anchorEl);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';

    const cleanSlugLink = slugLink.split('?')[0];
    return `${window.location.origin}/housing/${cleanSlugLink}`;
  }, [slugLink]);

  const emailSubject = t('emailSubject', { title });
  const emailBody = t('emailBody');
  const shareText = t('shareText');

  const handleShare = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      onToggle?.(true);
    },
    [onToggle],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onToggle?.(false);
    onClose?.();
  }, [onToggle, onClose]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(shareUrl);
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 4000);
  }, [shareUrl]);

  const trackSocial = useCallback((social_channel: string) => {
    pushClickSocial(social_channel);
  }, []);

  const shareButtons = (
    <Box sx={{ p: 1 }}>
      <ShareBtnBox>
        <WhatsappShareButton
          url={shareUrl}
          title={shareText}
          beforeOnClick={() => {
            trackSocial('WhatsApp');
          }}
        >
          <WhatsappIcon size={32} style={{ borderRadius: '4px' }} />
        </WhatsappShareButton>

        <FacebookShareButton
          url={shareUrl}
          hashtag="#AlleImmobilienAt"
          beforeOnClick={() => {
            trackSocial('Facebook');
          }}
        >
          <FacebookIcon size={32} style={{ borderRadius: '4px' }} />
        </FacebookShareButton>

        <PinterestShareButton
          url={shareUrl}
          media={media || ''}
          description={[title, description].filter(Boolean).join('\n\n')}
          beforeOnClick={() => {
            trackSocial('Pinterest');
          }}
        >
          <PinterestIcon size={32} style={{ borderRadius: '4px' }} />
        </PinterestShareButton>

        <LinkedinShareButton
          url={shareUrl}
          title={emailSubject}
          summary={shareText}
          beforeOnClick={() => {
            trackSocial('LinkedIn');
          }}
        >
          <LinkedinIcon size={32} style={{ borderRadius: '4px' }} />
        </LinkedinShareButton>

        <TwitterShareButton
          url={shareUrl}
          title={shareText}
          beforeOnClick={() => {
            trackSocial('X');
          }}
        >
          <XIcon size={32} style={{ borderRadius: '4px' }} />
        </TwitterShareButton>

        <EmailShareButton url={shareUrl} subject={emailSubject} body={emailBody}>
          <EmailIcon size={32} style={{ borderRadius: '4px' }} />
        </EmailShareButton>

        <ButtonCopyStyled
          $isCopied={isCopied}
          size="small"
          variant="outlined"
          onClick={handleCopy}
          aria-label={isCopied ? 'Link copied' : 'Copy link'}
        >
          {!isCopied ? (
            <ContentCopyIcon aria-hidden="true" />
          ) : (
            <LibraryAddCheckOutlinedIcon aria-hidden="true" />
          )}
        </ButtonCopyStyled>
      </ShareBtnBox>
    </Box>
  );

  if (isControlled) {
    return (
      <Popover
        id={id}
        open={open}
        onClose={handleClose}
        anchorEl={anchorElProp ?? null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
      >
        {shareButtons}
      </Popover>
    );
  }

  return (
    <>
      <span onClick={handleShare} style={{ display: 'inline-block' }}>
        {trigger}
      </span>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableScrollLock
      >
        {shareButtons}
      </Popover>
    </>
  );
};
